import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import PaystackCheckout from '../components/PaystackCheckout';
import LandingNavbar from '../components/LandingNavbar';

const NAVY     = '#1B2A5E';
const GOLD     = '#C9A84C';
const GOLD_DIM = '#A07C28';

const PROG_ACCENTS = ['#1B2A5E','#2D3F7A','#C9A84C','#A07C28','#243570'];
const PROG_ICONS   = ['ri-briefcase-4-line','ri-scales-line','ri-global-line','ri-bar-chart-box-line','ri-earth-line'];

function slugify(str) {
  return str
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

const ADMISSION_WINDOWS = ['JAN','APR','SEP'];

export default function ProgrammeDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [programme, setProgramme] = useState(null);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [admWindow, setAdmWindow] = useState('JAN');
  const [allProgs, setAllProgs] = useState([]);

  useEffect(() => {
    api.get('/academic/programmes/public')
      .then(r => {
        setAllProgs(r.data);
        const found = r.data.find(p => slugify(p.name) === slug);
        setProgramme(found || null);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (!programme || !user) return;
    const fetchCourses = async () => {
      try {
        const r = await api.get(`/academic/programmes/${programme.programme_id}/courses`);
        setCourses(r.data);
      } catch {
        setCourses([]);
      }
    };
    fetchCourses();
  }, [programme, user]);

  const progIndex = allProgs.findIndex(p => slugify(p.name) === slug);
  const accent = PROG_ACCENTS[progIndex >= 0 ? progIndex % PROG_ACCENTS.length : 0];
  const icon   = PROG_ICONS[progIndex >= 0 ? progIndex % PROG_ICONS.length : 0];

  const alreadyEnrolled = user?.enrolled_programmes?.some(e => e.programme_id === programme?.programme_id);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Inter, sans-serif', color: '#94A3B8' }}>
        Loading programme...
      </div>
    );
  }

  if (!programme) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', fontFamily: 'Inter, sans-serif' }}>
        <div style={{ fontSize: '1.1rem', color: NAVY, fontWeight: 600, marginBottom: '1rem' }}>Programme not found.</div>
        <button onClick={() => navigate('/programmes')} style={{ color: GOLD, background: 'none', border: 'none', cursor: 'pointer', fontSize: '.9rem' }}>← Back to all programmes</button>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#F8FAFC', fontFamily: 'Inter, sans-serif' }}>
      <LandingNavbar />

      {/* Breadcrumb — below fixed navbar */}
      <div style={{ background: '#FFF', borderBottom: '1px solid #C8D0E8', padding: '.75rem 2rem', display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '5rem' }}>
        <a href="/programmes" style={{ background: 'none', border: 'none', color: NAVY, cursor: 'pointer', fontSize: '.875rem', display: 'flex', alignItems: 'center', gap: '.4rem', fontWeight: 500, textDecoration: 'none' }}>
          <i className="ri-arrow-left-line" /> All Programmes
        </a>
        <span style={{ color: '#C8D0E8' }}>›</span>
        <span style={{ fontWeight: 600, color: '#475569', fontSize: '.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 480 }}>{programme.name}</span>
      </div>

      {/* Hero */}
      <div style={{ background: `linear-gradient(135deg, rgba(27,42,94,.06) 0%, rgba(201,168,76,.05) 100%)`, borderBottom: '1px solid #C8D0E8' }}>
        <style>{`
          @media(max-width:700px){.pd-hero-grid{grid-template-columns:1fr!important}.pd-enrol-card{min-width:unset!important;width:100%!important}}
          @media(max-width:480px){.pd-meta-row{flex-direction:column!important;gap:.75rem!important}}
        `}</style>
        <div className="pd-hero-grid" style={{ maxWidth: 1100, margin: '0 auto', padding: '3rem 1.5rem', display: 'grid', gridTemplateColumns: '1fr auto', gap: '2rem', alignItems: 'start' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '.75rem', marginBottom: '1.25rem' }}>
              <div style={{ width: 52, height: 52, borderRadius: 14, background: `${accent}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i className={icon} style={{ fontSize: '1.6rem', color: accent }} />
              </div>
              {programme.level && (
                <span style={{ fontSize: '.75rem', fontWeight: 700, padding: '.3rem .75rem', borderRadius: 6, background: 'rgba(201,168,76,.12)', color: GOLD_DIM, border: '1px solid rgba(201,168,76,.3)' }}>{programme.level}</span>
              )}
              {programme.code && (
                <span style={{ fontSize: '.75rem', fontWeight: 700, color: '#94A3B8' }}>{programme.code}</span>
              )}
            </div>
            <h1 style={{ fontSize: 'clamp(1.5rem,3.5vw,2.25rem)', fontWeight: 800, color: NAVY, margin: '0 0 1rem', lineHeight: 1.2 }}>{programme.name}</h1>
            <p style={{ fontSize: '1rem', color: '#475569', lineHeight: 1.7, maxWidth: 600, margin: '0 0 1.5rem' }}>{programme.description}</p>
            <div className="pd-meta-row" style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
              {[
                { icon: 'ri-time-line',          label: 'Duration', value: programme.duration },
                { icon: 'ri-book-open-line',      label: 'Courses',  value: `${programme.course_count || courses.length || 0} courses` },
                { icon: 'ri-graduation-cap-line', label: 'Level',    value: programme.level },
                { icon: 'ri-building-line',       label: 'Faculty',  value: programme.faculty || programme.department },
              ].filter(m => m.value).map(m => (
                <div key={m.label} style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
                  <i className={m.icon} style={{ color: GOLD, fontSize: '1rem' }} />
                  <div>
                    <div style={{ fontSize: '.7rem', color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em' }}>{m.label}</div>
                    <div style={{ fontSize: '.875rem', color: NAVY, fontWeight: 600 }}>{m.value}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Enrol card */}
          <div className="pd-enrol-card" style={{ background: '#FFF', border: '1px solid #C8D0E8', borderRadius: 16, padding: '1.75rem', minWidth: 280, boxShadow: '0 4px 24px rgba(27,42,94,.08)' }}>
            <div style={{ fontSize: '.75rem', color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: '.25rem' }}>Programme Fee</div>
            <div style={{ fontSize: '2rem', fontWeight: 800, color: GOLD, marginBottom: '.25rem' }}>
              GHS {Number(programme.price || 0).toLocaleString()}
            </div>
            <div style={{ fontSize: '.8rem', color: '#94A3B8', marginBottom: '1.5rem' }}>per semester</div>

            {alreadyEnrolled ? (
              <div>
                <div style={{ padding: '.75rem', background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 8, color: '#15803D', fontSize: '.85rem', fontWeight: 600, textAlign: 'center', marginBottom: '1rem' }}>
                  ✓ You are enrolled in this programme
                </div>
                <button
                  onClick={() => navigate('/student/home')}
                  style={{ width: '100%', padding: '.75rem', background: NAVY, color: '#FFF', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: '.9rem', cursor: 'pointer' }}
                >Go to Dashboard →</button>
              </div>
            ) : user ? (
              <div>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ fontSize: '.78rem', fontWeight: 600, color: '#475569', display: 'block', marginBottom: '.4rem' }}>Intake / Admission Window</label>
                  <div style={{ display: 'flex', gap: '.5rem' }}>
                    {ADMISSION_WINDOWS.map(w => (
                      <button
                        key={w}
                        onClick={() => setAdmWindow(w)}
                        style={{ flex: 1, padding: '.5rem', borderRadius: 7, border: `1px solid ${admWindow === w ? GOLD : '#C8D0E8'}`, background: admWindow === w ? 'rgba(201,168,76,.12)' : '#FFF', color: admWindow === w ? GOLD_DIM : '#475569', fontSize: '.8rem', fontWeight: 600, cursor: 'pointer' }}
                      >{w}</button>
                    ))}
                  </div>
                </div>
                <PaystackCheckout programme={programme} admissionWindow={admWindow} />
                <p style={{ fontSize: '.72rem', color: '#94A3B8', textAlign: 'center', marginTop: '.75rem' }}>
                  <i className="ri-lock-line" /> Secured Payment
                </p>
              </div>
            ) : (
              <div>
                <a
                  href="https://homeunilearn.com/auth/login"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: 'block', width: '100%', padding: '.8rem', background: NAVY, color: '#FFF', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: '.9rem', cursor: 'pointer', marginBottom: '.75rem', textAlign: 'center', textDecoration: 'none', boxSizing: 'border-box' }}
                >Sign In to Enrol</a>
                <div style={{ textAlign: 'center', fontSize: '.82rem', color: '#64748B' }}>
                  No account?{' '}
                  <a
                    href="https://homeunilearn.com/auth/login"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: GOLD, fontWeight: 600, fontSize: '.82rem', textDecoration: 'none' }}
                  >Create an account →</a>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Courses list */}
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '2.5rem 1.5rem' }}>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: NAVY, marginBottom: '1.5rem' }}>
          <i className="ri-book-2-line" style={{ marginRight: '.5rem', color: GOLD }} />
          Courses in this Programme
        </h2>

        {courses.length > 0 ? (
          <div style={{ display: 'grid', gap: '.75rem' }}>
            {courses.map((course, idx) => (
              <div key={course.course_id} style={{ background: '#FFF', border: '1px solid #C8D0E8', borderRadius: 10, padding: '1.1rem 1.25rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div style={{ width: 36, height: 36, borderRadius: 8, background: 'rgba(201,168,76,.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '.85rem', fontWeight: 700, color: GOLD_DIM }}>
                  {idx + 1}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, color: NAVY, fontSize: '.9rem' }}>{course.title}</div>
                  {course.code && <div style={{ fontSize: '.75rem', color: '#94A3B8', marginTop: '.15rem' }}>{course.code}</div>}
                </div>
                {user ? (
                  <i className="ri-arrow-right-s-line" style={{ color: '#CBD5E1' }} />
                ) : (
                  <span style={{ fontSize: '.72rem', color: '#94A3B8', display: 'flex', alignItems: 'center', gap: '.25rem' }}>
                    <i className="ri-lock-line" /> Enrol to access
                  </span>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div style={{ background: '#FFF', border: '1px solid #C8D0E8', borderRadius: 12, padding: '2rem', textAlign: 'center' }}>
            <i className="ri-book-open-line" style={{ fontSize: '2rem', color: '#CBD5E1', display: 'block', marginBottom: '.75rem' }} />
            <div style={{ color: '#94A3B8', fontSize: '.9rem' }}>
              {user ? `${programme.course_count || 0} courses available after enrolment` : 'Sign in to preview course content'}
            </div>
            {!user && (
              <a
                href="https://homeunilearn.com/auth/login"
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: 'inline-block', marginTop: '1rem', padding: '.6rem 1.25rem', background: NAVY, color: '#FFF', borderRadius: 8, fontWeight: 600, fontSize: '.85rem', textDecoration: 'none' }}
              >Sign In to Preview</a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
